import { useEffect, useState, useCallback } from 'react'
import { Network, Search, RefreshCw, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Cluster {
  uuid: string
  value: string
  description: string
  synonyms: string[]
  country: string
  motive: string[]
  sectors: string[]
  refs: string[]
  galaxy: string
}

export default function MispGalaxyPage() {
  const [meta, setMeta] = useState<any>(null)
  const [results, setResults] = useState<Cluster[]>([])
  const [q, setQ] = useState('')
  const [galaxy, setGalaxy] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const fetchMeta = useCallback(async () => {
    try {
      const r = await api.get('/api/sec/misp-galaxy')
      setMeta(r.data)
    } catch {}
  }, [])

  useEffect(() => { fetchMeta() }, [fetchMeta])

  const search = async () => {
    if (!q.trim()) return
    setLoading(true)
    try {
      const r = await api.get('/api/sec/misp-galaxy', {
        params: { q: q.trim(), galaxy: galaxy || undefined, limit: 80 },
      })
      setResults(r.data?.results || [])
    } finally { setLoading(false) }
  }

  const refresh = async () => {
    if (!confirm('Pull latest MISP galaxies from GitHub? Takes ~30s.')) return
    setRefreshing(true)
    try {
      await api.post('/api/sec/internal/refresh-misp-galaxy')
      await fetchMeta()
    } finally { setRefreshing(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Network size={24} className="text-purple" /> MISP Galaxy
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Community threat-actor / ransomware / malware corpus from <a href="https://github.com/MISP/misp-galaxy" target="_blank" rel="noreferrer" className="text-blue-light hover:underline">MISP/misp-galaxy</a>. Synonyms, motives, target sectors.
          </p>
        </div>
        <button type="button" onClick={refresh} disabled={refreshing} className="btn-ghost text-xs">
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
          {meta?.stale ? ' Fetch galaxies' : ' Re-fetch'}
        </button>
      </div>

      {meta && (
        <div className="card p-3 text-[11px] text-text-secondary">
          {meta.stale
            ? <>No galaxies cached yet. Click <em>Fetch galaxies</em> to pull from GitHub.</>
            : <>
              <strong>{meta.total}</strong> entries across {Object.keys(meta.galaxies || {}).length} galaxies
              · refreshed {meta.generated_at?.slice(0, 16).replace('T', ' ')}
              <div className="mt-1 flex flex-wrap gap-2">
                {Object.entries(meta.galaxies).map(([g, n]) => (
                  <span key={g} className="badge text-[9px] bg-surface-raised border border-border text-text-muted px-1.5">
                    {g} <span className="text-text-primary ml-1">{n as number}</span>
                  </span>
                ))}
              </div>
            </>}
        </div>
      )}

      <div className="card p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1 flex-1 min-w-[260px]">
            <span className="text-[10px] text-text-muted uppercase tracking-wider">Search</span>
            <input value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search()}
              placeholder='e.g. "lazarus" / "wiper" / "russia" / "FIN7"'
              className="input w-full py-1.5 text-xs" />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] text-text-muted uppercase tracking-wider">Galaxy</span>
            <select value={galaxy} onChange={e => setGalaxy(e.target.value)} className="input py-1.5 text-xs">
              <option value="">Any</option>
              {meta && Object.keys(meta.galaxies || {}).map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <button type="button" onClick={search} disabled={loading || !q.trim() || meta?.stale}
            className="btn-primary text-xs">
            {loading ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
            Search
          </button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {results.map(c => (
            <div key={c.uuid} className="card p-3 border border-border">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-text-primary">{c.value}</h3>
                <span className="badge text-[9px] bg-purple/15 text-purple-300 border border-purple/30">{c.galaxy}</span>
              </div>
              {c.country && <div className="text-[10px] text-text-muted mb-1">🌍 {c.country}</div>}
              <p className="text-[11px] text-text-secondary mb-2 line-clamp-3">{c.description || '—'}</p>
              {c.synonyms?.length > 0 && (
                <div className="text-[10px] text-text-muted mb-1">
                  <strong>Aka:</strong> {c.synonyms.slice(0, 5).join(', ')}{c.synonyms.length > 5 && ` +${c.synonyms.length - 5}`}
                </div>
              )}
              {c.motive?.length > 0 && (
                <div className="text-[10px] text-amber-light mb-1">Motive: {c.motive.join(', ')}</div>
              )}
              {c.sectors?.length > 0 && (
                <div className="text-[10px] text-blue-light mb-1">Targets: {c.sectors.slice(0, 3).join(', ')}</div>
              )}
              {c.refs?.[0] && (
                <a href={c.refs[0]} target="_blank" rel="noreferrer" className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1 mt-1">
                  reference <ExternalLink size={9} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {q && !loading && results.length === 0 && (
        <p className="card p-6 text-center text-text-muted text-[11px] italic">No matches.</p>
      )}
    </div>
  )
}
